'use client';

import Image from 'next/image';
import { useRef, Fragment } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import ChipText from '@/components/global/ChipText';
import {GROQImage} from "@/sanity/lib/types";

interface IBulletProps {
  chipText: string;
  title: PortableTextBlock[];
  items: {
    text: string;
  }[];
}

export default function Bullet({
  chipText,
  title,
  items,
}: IBulletProps) {
  const isMobile = useIsMobile();

  return (
    <section className="flex w-full bg-[#072922] relative px-[2rem] lg:px-[51.13rem]">
      <div className="flex flex-col lg:gap-[12rem] items-center w-full relative px-[1.75rem] lg:px-[18.13rem] py-[13rem] lg:pt-[16.12rem] lg:pb-[17.38rem] border-x border-green/10">
        <div className="flex flex-col items-center gap-[2rem] lg:pt-[2rem] mb-[6.25rem] lg:mb-0">
          {chipText &&
            <ChipText text={chipText} color="green" />
          }
          <div className="richtext richtext-center flex flex-col gap-[3rem] prose-headings:text-offwhite prose-p:text-[2rem] lg:prose-p:text-[3rem] prose-p:font-[300] prose-p:text-offwhite/50">
            <PortableText value={title} components={richTextComponent} />
          </div>
        </div>
        
        <div className="flex flex-col gap-[5rem] px-[2rem]">
          {items?.length > 0 && items?.map((item, index) => (
            <Fragment key={index}>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: easeOut, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="flex flex-row pl-[2rem] gap-[2rem]"
              >
                <div className="w-[1.25rem] h-[1.25rem] rounded-full bg-offwhite/[72%] flex-shrink-0 mt-[1.5rem]"/>
                <p className="text-[2.5rem] lg:text-[4rem] font-[300] text-offwhite/[72%] leading-[140%] lg:leading-[120%] mb-0">{item.text}</p>
              </motion.div>

              {index < items.length - 1 && (
                <motion.div
                  className="w-full h-0.5 self-stretch bg-green/[24%]"              
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, ease: easeOut, delay: index * 0.15 + 0.3 }}
                  viewport={{ once: true }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
